
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Row, Table, Button, Descriptions, Col, Form, Modal, message, Upload, Switch } from 'antd'
import { UploadOutlined, InboxOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons'
import { getAllImportTasks, getImportTasksById, submitUploadAttributeFile } from '@services/attributeService'
import './index.scss'
import { showError } from '@utils/alert'
import moment from 'moment'
import { hashcode } from '@utils/index'

const { Dragger } = Upload

function UploadProductAttributeDetail(props) {
  const { task, visible, onCancel } = props
  const { id, status, user_id, file } = task || {}
  const [pageSize, setPageSize] = useState(20)
  const [histories, setHistories] = useState([])
  const [total, setTotal] = useState(0)
  const [current, setCurrent] = useState(1)

  const columns = [
    {
      title: '#Num',
      key: 'index',
      render: (t, r, index) => ((current - 1) * pageSize) + index + 1,
    },
    {
      title: 'Id',
      dataIndex: 'id',
      key: 'id',
      width: 200,
    }, {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
    }, {
      title: 'Reason',
      dataIndex: 'reason',
      key: 'reason',
      width: 300,
    }
  ]

  useEffect(() => {
    const f = current - 1
    const t = current * pageSize
    getImportTasksById(id, f, t)
      .then(res => {
        if (res.data?.total) {
          setTotal(res.data?.total)
        }
        if (Array.isArray(res.data?.histories)) {
          setHistories(res.data.histories)
        }
      })
  }, [id, current, pageSize])

  return (
    <Modal
      visible={visible}
      title={`Task Detail`}
      closable
      centered
      onCancel={onCancel}
      width={800}
    >
      <Descriptions bordered size="small" layout="vertical">
        <Descriptions.Item label="Id">{id}</Descriptions.Item>
        <Descriptions.Item label="Author">{user_id}</Descriptions.Item>
        <Descriptions.Item label="Status">{status}</Descriptions.Item>
        <Descriptions.Item label="File">{file?.name || ''}</Descriptions.Item>
      </Descriptions>
      <Table
        style={{ backgroundColor: '#ffffff' }}
        tableLayout="fixed"
        columns={columns}
        size="small"
        dataSource={histories || []}
        rowKey={(record: any) => record.id}
        pagination={{
          pageSize: pageSize,
          total: total || 0,
          showQuickJumper: true,
          showSizeChanger: true,
          hideOnSinglePage: true,
          pageSizeOptions: ['20', '50', '100'],
          current: current,
          onChange: (page) => setCurrent(page),
          onShowSizeChange: (_, pageSize) => setPageSize(pageSize)
        }}
      />
    </Modal>
  )
}

function UploadProductAttributes() {
  const [uploadFiles, setUploadFiles]: [any[], any] = useState([])
  const [tableLoading, setTableLoading] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [dialogVisible, setDialogVisible] = useState(false)
  const [autoRefreshing, setAutoRefreshing] = useState(true)
  const [currentTask, setCurrentTask] = useState<any>(null)
  const [list, setList] = useState([])
  const timerHandler = useRef<any>(null)

  function onEdit(record) {
    if (dialogVisible) return
    setDialogVisible(true)
    setCurrentTask(record)
  }

  const columns = [
    {
      title: 'Id',
      dataIndex: 'id',
      key: 'id',
      width: 350,
    }, {
      title: 'Owner',
      dataIndex: 'user_id',
      key: 'user_id',
      width: 350,
    }, {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
    }, {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: 100,
    }, {
      title: 'Created At',
      dataIndex: 'created_at',
      width: 200,
      render: (record) => {
        return moment(record).format('YYYY-MM-DD HH:mm')
      }
    }, {
      title: 'Actions',
      key: 'action',
      width: '250px',
      render: (record) => (
        <Row>
          <Col span={8}>
            <Button
              disabled={record.status === 'doing'}
              loading={record.status === 'doing'}
              type="primary"
              onClick={() => onEdit(record)}
            >
              {record.status === 'doing' ? '处理中' : '查看详情'}
            </Button>
          </Col>
        </Row>
      )
    }
  ]

  function handleUpload() {
    if (uploadFiles.length < 0) return
    stopTimer()
    setUploading(true)
    const hide = message.loading('上传中')
    const file = uploadFiles[0]
    let name = file.name
    let extension = ''
    if (file.name && file.name.indexOf('.') !== -1) {
      name = file.name.substring(0, file.name.lastIndexOf('.'))
      extension = file.name.substring(file.name.lastIndexOf('.'))
    }
    const form = new FormData()
    form.append('file', file, hashcode(name) + extension)
    submitUploadAttributeFile(form)
      .then(res => {
        message.success('提交成功')
        handleSearch()
        hide()
        setUploading(false)
      })
      .catch(e => {
        hide()
        showError(e)
        console.error(e)
        setUploading(false)
      })
  }

  const handleSearch = useCallback(() => {
    setTableLoading(true)
    getAllImportTasks()
      .then(res => {
        setTableLoading(false)
        if (res.data?.tasks?.length) {
          setList(res.data.tasks)
          timerHandler.current = setTimeout(() => {
            if (autoRefreshing) {
              handleSearch()
            }
          }, 10000)
        }
      })
      .catch(e => {
        console.error(e)
        showError(e)
        setTableLoading(false)
      })
  }, [autoRefreshing])

  const stopTimer = useCallback(() => {
    timerHandler.current && clearTimeout(timerHandler.current)
  }, [])

  useEffect(() => {
    handleSearch()
    return () => {
      stopTimer()
    }
  }, [handleSearch, stopTimer])

  const draggerProps = {
    name: 'file',
    maxCount: 1,
    accept: '.xlsx',
    onRemove: (file: any) => {
      console.log(file)
      const index = uploadFiles.indexOf(file)
      if (index > -1) {
        uploadFiles.splice(index, 1)
        setUploadFiles([...uploadFiles])
      }
    },
    beforeUpload: (file: any) => {
      setUploadFiles([file])
      return false
    },
    onDrop(e) {
      console.log('Dropped files', e.dataTransfer.files)
    },
  }

  return (
    <div className="task-attributes">
      <Form layout="inline" name="attribute-upload">
        <Form.Item>
          <Dragger {...draggerProps} style={{ minWidth: '380px' }}>
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">Click or drag the file to upload</p>
            <p className="ant-upload-hint">
              Support for .xlsx file only.
            </p>
          </Dragger>
        </Form.Item>
        <Form.Item>
          <Button
            disabled={uploadFiles.length === 0}
            loading={uploading}
            icon={<UploadOutlined />}
            type="primary"
            onClick={handleUpload}
          >
            上传
          </Button>
        </Form.Item>
      </Form>
      <div style={{ padding: '4px 0 12px 0' }}>
        <span>自动刷新(10s): </span>
        <Switch
          checkedChildren={<CheckOutlined />}
          unCheckedChildren={<CloseOutlined />}
          checked={autoRefreshing}
          onChange={setAutoRefreshing}
        />
      </div>
      <Table
        style={{ backgroundColor: '#ffffff' }}
        tableLayout="fixed"
        columns={columns}
        dataSource={list}
        loading={tableLoading}
        rowKey={(record: any) => record.id}
        pagination={false}
      />
      <UploadProductAttributeDetail
        visible={dialogVisible}
        task={currentTask}
        onCancel={() => {
          setDialogVisible(false)
          setCurrentTask(null)
        }}
      />
    </div>
  )
}

export default UploadProductAttributes
